// API funcional para abrir los modales genéricos de aviso/confirmación (se llaman directo, ej. `await confirm(...)`).
// Es la versión NUEVA/recomendada para código nuevo: existe en paralelo `@/composables/modalHelperVue.ts`, una API
// más vieja basada en composables (`useConfirm().show(...)`, `useWarn().show(...)`) que sigue viva porque la usan
// IntegrationsModal.vue, Integrations.vue, InternalNews*.vue, TagsManager.vue y SearchCreateSelect.vue — no migrar
// esos usos sin coordinarlo, pero para código nuevo preferir siempre las funciones de este archivo.
// Ambos archivos renderizan los MISMOS componentes (ConfirmModal.vue, PolyWarnModal.vue), así que el estilo visual
// (ver tipos `type`/`destructive` más abajo) es compartido entre las dos APIs.
//
// Funciones expuestas:
// - `confirm(title, text, opts?)`: sí/no genérico, 2 botones. `opts.destructive` pinta el botón de confirmar en rojo
//   (usar SOLO para borrados definitivos, ej. `RetailMediaAdvertisers.vue`/`RetailMediaCircuits.vue`).
// - `confirmWithList(data)`: como `confirm`, pero con una lista de ítems afectados debajo del texto (ej. campañas
//   que se van a desvincular). Ver `RetailMediaAdvertisers.vue`.
// - `warnModal(data)`: aviso de 1 solo botón con ícono, sin opción de cancelar. `data.type` ('warning'/'success'/
//   'error') define ícono+color; `data.icon` permite un ícono custom en violeta para casos sin tipo predefinido.
//   Ver `RetailMediaCircuits.vue`.
// - `confirmUnsavedChanges(title, text)` / `confirmActivateCampaign(title, text)` / `confirmReserveDraft(title, text)`:
//   variantes de 3 botones sobre `UnsavedChangesModal.vue` (volver / acción secundaria / acción principal). Ver
//   `RetailMediaNewCampaign.vue`.
// - `mediaHardwareFeedbackModal(data)`: avisa que un media a subir excede la resolución soportada por el hardware
//   de reproducción, ofreciendo contratar el Conversor u optimizar el archivo por su cuenta. Ver
//   `mediaHardwareInspector.service.ts`.
// - `mediaHardwareConfirmationModal()`: confirma que se envió el mail de activación del Conversor. Ver
//   `mediaHardwareInspector.service.ts`.
import { useModal, VueFinalModal, useModalSlot } from "vue-final-modal";
import ModalWrapper from "@/modals/ModalWrapper.vue";
import type { Component } from "vue";
import ConfirmModal from "@/modals/ConfirmModal.vue";
import PolyWarnModal from "@/modals/PolyWarnModal.vue";
import UnsavedChangesModal from "@/modals/UnsavedChangesModal.vue";
import MediaHardwareFeedbackModal from "@/modals/MediaHardwareFeedbackModal.vue";
import MediaHardwareConfirmationModal from "@/modals/MediaHardwareConfirmationModal.vue";
import type { InspectedMedia, MediaHardwareFeedbackResult } from "@/interfaces/mediaHardwareInspector.interfaces";
import type { VNodeChild } from "vue";
import type { UseModalOptions } from "vue-final-modal";

// z-index para un modal desde el que se abren diálogos AngularJS: queda por debajo de $mdDialog (80) y de ntnConfirm
// (100), que con el z-index por defecto de vue-final-modal (1000+) quedarían tapados. Usar con cfg.zIndex
export const BELOW_ANGULAR_DIALOGS_Z_INDEX = 70;

interface returnType {
  open: Function;
}

interface modalCfg {
  title: string;
  lateral?: Boolean;
  // Variante de header blanco/texto oscuro con botón de cerrar bordeado, en vez del header violeta por
  // default (ver ModalWrapper.vue, modificador ".plain-header"). Ver ExportDataModal.vue.
  plainHeader?: boolean;
  width?: string;
  height?: string;
  header?: boolean;
  onBeforeOpen?: () => any;
  onOpened?: () => any;
  onBeforeClose?: () => any;
  onClosed?: () => any;
  escToClose?: boolean;
  clickToClose?: boolean;
  clickOutsideToClose?: boolean;
  // z-index fijo del modal, en vez del de vue-final-modal (1000+). Ver BELOW_ANGULAR_DIALOGS_Z_INDEX
  zIndex?: number;
}

// Frena el Escape en el modal: vue-final-modal lo usa para cerrar, pero lo deja subir hasta el body, donde Onsen (la
// navegación de mobile) lo toma como "atrás" y saca la pantalla de abajo del modal
// (los tipos de VueFinalModal no declaran onKeydown, pero lo pasa como atributo a su div raíz)
const escapeGuard: any = {
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') event.stopPropagation();
  },
};

export function useModalHelper<DataType>(
  component: Component,
  data: any,
  cfg?: modalCfg
): { open: () => Promise<DataType> } {
  const { open: _open, close: _close } = useModal({
    component: VueFinalModal,
    // Configuraciones de VFM
    attrs: {
      // Configuración
      escToClose: cfg?.hasOwnProperty("escToClose") ? cfg.escToClose : true,
      ...(cfg?.zIndex !== undefined ? { zIndexFn: () => cfg.zIndex } : {}),
      overlayTransition: "vfm-fade",
      contentTransition: (cfg?.hasOwnProperty("lateral") ? cfg.lateral : true)
        ? "vfm-slide-right"
        : "vfm-fade",
      contentClass: `modalhelper${(cfg?.hasOwnProperty("lateral") ? cfg.lateral : true) ? " lateral" : ""
        }${cfg?.plainHeader ? " plain-header" : ""}`,

      // Emits
      onBeforeOpen: cfg?.onBeforeOpen,
      onOpened: cfg?.onOpened,
      onBeforeClose() {
        if (cfg?.onBeforeClose) cfg.onBeforeClose();
      },
      onClosed: cfg?.onClosed,
      ...escapeGuard,
    },
    slots: {
      default: useModalSlot({
        component: ModalWrapper,
        attrs: <any>{
          // Configuración de ModalWrapper
          lateral: cfg?.hasOwnProperty("lateral") ? cfg.lateral : true,
          width: cfg?.width,
          height: cfg?.height,
          header: cfg?.hasOwnProperty("header") ? cfg.header : true,
          // Atributos que pasan al componente del propio modal
          title: cfg?.title || "",
          resolve(data: any) {
            resolveModal(data);
          },
          reject(err: Error) {
            rejectModal(err);
          },
          modalComponent: component,
          modalProps: data as Object,
        },
      }),
    },
  });

  type ReturnPromise = Promise<DataType>;

  let promise: ReturnPromise;
  let resolver: Function;
  let rejecter: Function;

  async function open(): ReturnPromise {
    try {
      promise = new Promise((resolve, reject) => {
        resolver = resolve;
        rejecter = reject;
      });
      await _open();
      return promise;
    } catch (error) {
      throw new Error(`${error}`);
    }
  }

  function resolveModal<T>(resolve: T) {
    _close();
    resolver(resolve);
  }

  function rejectModal(err: Error) {
    rejecter(err);
  }

  return {
    open: open,
  };
}

export function confirm(title: string, text: string, opts?: { destructive?: boolean; confirmText?: string; cancelText?: string }): Promise<boolean> {
  const { open: _open, close } = useModal({
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: ConfirmModal,
        attrs: {
          title: title,
          text: text,
          resolve: resolve,
          destructive: opts?.destructive,
          confirmText: opts?.confirmText,
          cancelText: opts?.cancelText,
        },
      }),
    },
  });
  let promise: Promise<boolean>;
  let resolver: Function;

  function resolve(value: boolean) {
    close();
    resolver(value);
  }

  promise = new Promise((resolve, reject) => {
    resolver = resolve;
  });

  _open();
  return promise;
}

// Advierte sobre cambios sin guardar antes de salir, con 3 opciones: guardar y salir, salir sin guardar, o cancelar (quedarse)
export function confirmUnsavedChanges(title: string, text: string): Promise<'save' | 'discard' | 'cancel'> {
  let resolver: (value: 'save' | 'discard' | 'cancel') => void = () => { };
  const promise: Promise<'save' | 'discard' | 'cancel'> = new Promise(
    (resolve) => (resolver = resolve)
  );

  const { open: _open, close } = useModal({
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: UnsavedChangesModal,
        attrs: {
          title: title,
          text: text,
          resolve: resolve,
        },
      }),
    },
  });

  function resolve(value: 'primary' | 'secondary' | 'cancel') {
    close();
    resolver(({ primary: 'save', secondary: 'discard', cancel: 'cancel' } as const)[value]);
  }

  _open();
  return promise;
}

// Confirma la activación de una campaña, con 3 opciones: confirmar y activar, guardar como borrador, o volver (quedarse editando)
export function confirmActivateCampaign(title: string, text: string): Promise<'activate' | 'draft' | 'cancel'> {
  let resolver: (value: 'activate' | 'draft' | 'cancel') => void = () => { };
  const promise: Promise<'activate' | 'draft' | 'cancel'> = new Promise(
    (resolve) => (resolver = resolve)
  );

  const { open: _open, close } = useModal({
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: UnsavedChangesModal,
        attrs: {
          title: title,
          text: text,
          resolve: resolve,
          cancelText: 'VOLVER',
          secondaryText: 'GUARDAR COMO BORRADOR',
          primaryText: 'CONFIRMAR Y ACTIVAR',
        },
      }),
    },
  });

  function resolve(value: 'primary' | 'secondary' | 'cancel') {
    close();
    resolver(({ primary: 'activate', secondary: 'draft', cancel: 'cancel' } as const)[value]);
  }

  _open();
  return promise;
}

// Pregunta si reservar el espacio de un borrador antes de guardarlo, con 3 opciones: reservar, no reservar (guardar normal), o cancelar (no hacer nada)
export function confirmReserveDraft(title: string, text: string): Promise<'reserve' | 'no-reserve' | 'cancel'> {
  let resolver: (value: 'reserve' | 'no-reserve' | 'cancel') => void = () => { };
  const promise: Promise<'reserve' | 'no-reserve' | 'cancel'> = new Promise(
    (resolve) => (resolver = resolve)
  );

  const { open: _open, close } = useModal({
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: UnsavedChangesModal,
        attrs: {
          title: title,
          text: text,
          resolve: resolve,
          cancelText: window.i18next.t('CANCELAR'),
          secondaryText: window.i18next.t('GUARDAR SIN RESERVAR'),
          primaryText: window.i18next.t('GUARDAR Y RESERVAR'),
        },
      }),
    },
  });

  function resolve(value: 'primary' | 'secondary' | 'cancel') {
    close();
    resolver(({ primary: 'reserve', secondary: 'no-reserve', cancel: 'cancel' } as const)[value]);
  }

  _open();
  return promise;
}

// Igual que confirm, pero con lista de ítems afectados: devuelve true/false según lo que elija el usuario
export function confirmWithList(data: {
  title: string;
  text?: string;
  list?: string[];
  bottomText?: string;
  destructive?: boolean;
}): Promise<boolean> {
  let resolver: (value: boolean) => void = () => { };
  const promise: Promise<boolean> = new Promise(
    (resolve, reject) => (resolver = resolve)
  );

  const modalConfig: UseModalOptions<any> = {
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: ConfirmModal,
        attrs: {
          resolve: resolve,
          title: data.title,
          text: data.text,
          list: data.list,
          bottomText: data.bottomText,
          destructive: data.destructive,
        },
      }),
    },
  };

  const { open, close } = useModal(modalConfig);

  function resolve(value: unknown) {
    close();
    resolver(!!value);
  }

  open();
  return promise;
}

export function warnModal(data: {
  title: string;
  text?: string;
  list?: string[];
  renderFunction?: () => VNodeChild;
  type?: 'warning' | 'success' | 'error';
  icon?: string;
  confirmText?: string;
}): Promise<void> {
  let resolver: () => void = () => { };
  const promise: Promise<void> = new Promise(
    (resolve, reject) => (resolver = resolve)
  );

  const modalConfig: UseModalOptions<any> = {
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: PolyWarnModal,
        attrs: {
          resolve: resolve,
          title: data.title,
          text: data.text,
          list: data.list,
          renderFunction: data.renderFunction,
          type: data.type,
          icon: data.icon,
          confirmText: data.confirmText,
        },
      }),
    },
  };

  const { open, close } = useModal(modalConfig);

  function resolve() {
    close();
    resolver();
  }

  open();
  return promise;
}

// Avisa que alguno de los medias a subir excede la resolución soportada por el hardware de reproducción, ofreciendo
// contratar el Conversor incorporado u optimizar el archivo por su cuenta. Usado por mediaHardwareInspector.service.ts
export function mediaHardwareFeedbackModal(data: {
  medias: InspectedMedia[];
  recommendedResolutionLabel: string;
  canHireConversor: boolean;
  converterPrice: string;
  totemsCount: number;
  converterTotalPrice: number;
}): Promise<MediaHardwareFeedbackResult> {
  let resolver: (value: MediaHardwareFeedbackResult) => void = () => { };
  const promise: Promise<MediaHardwareFeedbackResult> = new Promise(
    (resolve) => (resolver = resolve)
  );

  const modalConfig: UseModalOptions<any> = {
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: MediaHardwareFeedbackModal,
        attrs: { ...data, resolve },
      }),
    },
  };

  const { open, close } = useModal(modalConfig);

  function resolve(value: MediaHardwareFeedbackResult) {
    close();
    resolver(value);
  }

  open();
  return promise;
}

// Confirma que se envió el mail de activación del Conversor. Usado por mediaHardwareInspector.service.ts
export function mediaHardwareConfirmationModal(): Promise<void> {
  let resolver: () => void = () => { };
  const promise: Promise<void> = new Promise((resolve) => (resolver = resolve));

  const modalConfig: UseModalOptions<any> = {
    component: VueFinalModal,
    attrs: {
      escToClose: true,
      ...escapeGuard,
      clickToClose: true,
      overlayTransition: "vfm-fade",
      contentTransition: "vfm-fade",
      contentClass: "modalhelper",
    },
    slots: {
      default: useModalSlot({
        component: MediaHardwareConfirmationModal,
        attrs: { resolve },
      }),
    },
  };

  const { open, close } = useModal(modalConfig);

  function resolve() {
    close();
    resolver();
  }

  open();
  return promise;
}
